import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  input,
  OnChanges,
  signal,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {WaBarChartDirective, WaCardDirective, WaToastService} from "angular-awesome";
import {SurveyService} from "../../../api/services/survey.service";
import {lastValueFrom} from "rxjs";
import {SolutionQuality, UserSummary} from "../../../api/models/summary-data";
import {Survey} from "../../../api/models/survey";

@Component({
  selector: 'app-statistics-panel',
    imports: [
        WaBarChartDirective,
        WaCardDirective
    ],
  templateUrl: './statistics-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './statistics-panel.component.scss',
})
export class StatisticsPanelComponent implements OnChanges, AfterViewInit {

  survey = input.required<Survey>();
  loading = signal(false);
  error = signal<string | null>(null);

  userData = signal<UserSummary>({
    registered: 0, staff_members: 0, active_staff_members: 0, linked: 0, logged_in: 0, voted: 0
  });
  solutionData = signal<SolutionQuality>({
    iis: [],
    latest_failed: false,
    supervision: [],
    correction: [],
  })

  // totals for workload calculations
  requiredSupervisionsTotal = signal(0);
  requiredCorrectionsTotal = signal(0);
  availableSupervisionsTotal = signal(0);
  availableCorrectionsTotal = signal(0);
  // computed chart configuration: will update automatically when userData changes
  chartConfigUsersSignal = computed(() => {
    return {
      type: 'bar', data: {
        labels: ['Total Users', 'Active Staff Members', 'Logged In', 'Voted'], datasets: [{
          label: 'users',
          data: [this.userData().registered, this.userData().staff_members, this.userData().logged_in, this.userData().voted],
        }]
      }
    };
  });

  // computed chart configuration: will update automatically when userData changes
  chartConfigWorkloadSignal = computed(() => {
    return {
      type: 'bar', data: {
        labels: ['supervision', 'grading'], datasets: [{
          label: 'required', data: [this.requiredSupervisionsTotal(), this.requiredCorrectionsTotal()],
        }, {
          label: 'available', data: [this.availableSupervisionsTotal(), this.availableCorrectionsTotal()],
        }]
      }
    };
  });

  chartConfigOptimizationSignal = computed(() => {
    return {
      type: 'bar', data: {
        labels: ['supervision', 'grading'], datasets: [{
          label: 'required', data: [this.requiredSupervisionsTotal(), this.requiredCorrectionsTotal()],
        }, {
          label: 'available', data: [this.availableSupervisionsTotal(), this.availableCorrectionsTotal()],
        }]
      }
    };
  })

private stateValid() {
    return !!this.survey();
  }

  constructor(private surveyService: SurveyService, private toastService: WaToastService, private cd: ChangeDetectorRef) {
  }

  // when surveyId input changes, load the survey
  ngOnChanges(changes: SimpleChanges): void {
    this.getStatisticsData();
  }

  ngAfterViewInit(): void {
    // ensure the chart is initialized when the view is ready
    this.getStatisticsData();
  }

  private async getStatisticsData(): Promise<void> {
    if (!this.stateValid()) return;

    const survey = this.survey();
    const surveyId = survey!.id;

    try {
      const userSummary = await this.surveyService.getUserSummary(surveyId);
      this.userData.set(userSummary);
      const solutionSummary = await this.surveyService.getSolutionSummary(surveyId);
      this.solutionData.set(solutionSummary);
      // ensure Angular runs change detection so chart input changes are observed
      try {
        this.cd.detectChanges();
      } catch (_) {
      }

      // fetch all exams and compute required totals
      let totalRequiredSupervisions = 0;
      let totalRequiredCorrections = 0;
      const examsResult = await lastValueFrom(this.surveyService.getExams(surveyId));
      for (const ex of examsResult.data) {
        totalRequiredSupervisions += ex.main_exam.required_supervisors;
        totalRequiredCorrections += ex.main_exam.required_corrections;
        if (ex.has_repetition) {
          totalRequiredSupervisions += ex.retake_exam?.required_supervisors ?? 0;
          totalRequiredCorrections += ex.retake_exam?.required_corrections ?? 0;
        }
      }
      this.requiredSupervisionsTotal.set(totalRequiredSupervisions);
      console.log(totalRequiredCorrections);
      this.requiredCorrectionsTotal.set(totalRequiredCorrections);

      // fetch all staff members and compute available totals (sum of required_supervisions/required_corrections per staff)
      let totalAvailableSupervisions = 0;
      let totalAvailableCorrections = 0;
      const staffResult = await lastValueFrom(this.surveyService.getStaffMembers(surveyId));
      for (const sm of staffResult.data) {
        // prefer explicit fields on staffMember
        const sup = (typeof sm.required_supervisions === 'number') ? sm.required_supervisions : 0;
        const cor = (typeof sm.required_corrections === 'number') ? sm.required_corrections : 0;
        totalAvailableSupervisions += sup;
        totalAvailableCorrections += cor;
      }
      this.availableSupervisionsTotal.set(totalAvailableSupervisions);
      this.availableCorrectionsTotal.set(totalAvailableCorrections);


    } catch (error: any) {
      this.toastService.danger('Could not fetch solution quality data.');
    }
  }
}
